import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Clock, ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const DAYS = [
  { value: 0, label: "Lundi" },
  { value: 1, label: "Mardi" },
  { value: 2, label: "Mercredi" },
  { value: 3, label: "Jeudi" },
  { value: 4, label: "Vendredi" },
  { value: 5, label: "Samedi" },
  { value: 6, label: "Dimanche" },
];

const SLOT_TYPES = [
  { value: "coaching", label: "Coaching" },
  { value: "libre", label: "Libre" },
  { value: "individuel", label: "Individuel" },
];

export default function WeeklyScheduleManager() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [selectedDay, setSelectedDay] = useState(0); // 0 = Lundi
  
  const [formData, setFormData] = useState({
    day: 0,
    hour: 9,
    minute: 0,
    duration_min: 60,
    slot_type: "coaching",
    capacity: 3,
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/schedule");
      setTemplates(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement des créneaux");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        day: template.day,
        hour: template.hour,
        minute: template.minute,
        duration_min: template.duration_min,
        slot_type: template.slot_type,
        capacity: template.capacity,
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        day: selectedDay,
        hour: 9,
        minute: 0,
        duration_min: 60,
        slot_type: "coaching",
        capacity: 3,
      });
    }
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingTemplate) {
        await api.put(`/admin/schedule/${editingTemplate.id}`, formData);
        toast.success("Créneau modifié avec succès");
      } else {
        await api.post("/admin/schedule", formData);
        toast.success("Créneau ajouté avec succès");
      }
      setModalOpen(false);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };

  const handleDelete = async (templateId) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer ce créneau ?")) return;
    
    try {
      await api.delete(`/admin/schedule/${templateId}`);
      toast.success("Créneau supprimé");
      fetchTemplates();
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleDeleteAll = async () => {
    if (!confirm("⚠️ ATTENTION : Voulez-vous vraiment supprimer TOUS les créneaux du semainier ? Cette action est irréversible.")) return;
    
    try {
      // Delete all templates one by one
      await Promise.all(templates.map(t => api.delete(`/admin/schedule/${t.id}`)));
      toast.success("Tous les créneaux ont été supprimés");
      fetchTemplates();
    } catch (err) {
      toast.error("Erreur lors de la suppression massive");
    }
  };

  const handleDeleteDayTemplates = async () => {
    const dayLabel = getDayLabel(selectedDay);
    if (!confirm(`⚠️ Voulez-vous vraiment supprimer tous les créneaux de ${dayLabel} ? Cette action est irréversible.`)) return;
    
    try {
      const dayTemplates = groupedByDay[selectedDay] || [];
      await Promise.all(dayTemplates.map(t => api.delete(`/admin/schedule/${t.id}`)));
      toast.success(`Tous les créneaux de ${dayLabel} ont été supprimés`);
      fetchTemplates();
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const getDayLabel = (day) => DAYS.find(d => d.value === day)?.label || day;
  const getTypeLabel = (type) => SLOT_TYPES.find(t => t.value === type)?.label || type;
  const getTypeColor = (type) => {
    switch (type) {
      case "coaching": return { border: "border-blue-500", borderWidth: "border-2", text: "text-blue-400", badge: "bg-blue-600 hover:bg-blue-700" };
      case "libre": return { border: "border-green-500", borderWidth: "border-2", text: "text-green-400", badge: "bg-green-600 hover:bg-green-700" };
      case "individuel": return { border: "border-purple-500", borderWidth: "border-2", text: "text-purple-400", badge: "bg-purple-600 hover:bg-purple-700" };
      default: return { border: "border-stone-700", borderWidth: "border-2", text: "text-stone-400", badge: "bg-stone-600 hover:bg-stone-700" };
    }
  };

  // Group by day
  const groupedByDay = templates.reduce((acc, template) => {
    if (!acc[template.day]) acc[template.day] = [];
    acc[template.day].push(template);
    return acc;
  }, {});

  // Sort templates by time for each day
  Object.keys(groupedByDay).forEach(day => {
    groupedByDay[day].sort((a, b) => {
      if (a.hour !== b.hour) return a.hour - b.hour;
      return a.minute - b.minute;
    });
  });

  // Navigation helpers
  const navigateDay = (direction) => {
    if (direction === 'prev' && selectedDay > 0) {
      setSelectedDay(selectedDay - 1);
    } else if (direction === 'next' && selectedDay < DAYS.length - 1) {
      setSelectedDay(selectedDay + 1);
    }
  };

  const currentDayTemplates = groupedByDay[selectedDay] || [];

  return (
    <div className="space-y-6" style={{ backgroundColor: '#000000' }}>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Gestion du Semainier</h2>
          <p className="text-sm text-stone-400 mt-1">Configurez les créneaux hebdomadaires disponibles</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => handleOpenModal()} className="bg-white text-stone-900 hover:bg-stone-100">
            <Plus className="h-4 w-4 mr-2" />
            Ajouter un créneau
          </Button>
          <Button 
            onClick={handleDeleteAll} 
            variant="outline"
            disabled={templates.length === 0}
            className="border-red-700 text-red-400 hover:bg-red-900/20"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Supprimer tous
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 bg-stone-950 rounded-xl">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-stone-700 border-t-stone-400"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Horizontal Day Navigation */}
          <div className="flex items-center gap-2 bg-stone-950 p-4 rounded-xl overflow-x-auto" style={{ backgroundColor: '#0a0a0a' }}>
            {/* Previous day button */}
            <button
              onClick={() => navigateDay('prev')}
              disabled={selectedDay === 0}
              className="flex-shrink-0 p-2 rounded-lg border border-stone-700 bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              title="Jour précédent"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            
            {/* Day buttons */}
            <div className="flex gap-2 flex-1 overflow-x-auto pb-1">
              {DAYS.map((day) => {
                const isSelected = day.value === selectedDay;
                const hasTemplates = (groupedByDay[day.value] || []).length > 0;
                
                return (
                  <button
                    key={day.value}
                    onClick={() => setSelectedDay(day.value)}
                    className={`relative flex-shrink-0 px-4 py-3 rounded-lg font-medium text-sm transition-all ${
                      isSelected
                        ? 'bg-white text-stone-900 shadow-lg'
                        : 'bg-stone-900 text-stone-300 hover:bg-stone-800 border border-stone-700'
                    }`}
                  >
                    <span>{day.label}</span>
                    {hasTemplates && (
                      <span className={`absolute -top-1 -right-1 h-3 w-3 rounded-full ${isSelected ? 'bg-blue-500' : 'bg-orange-500'}`} title={`${groupedByDay[day.value].length} créneau(x)`}></span>
                    )}
                  </button>
                );
              })}
            </div>
            
            {/* Next day button */}
            <button
              onClick={() => navigateDay('next')}
              disabled={selectedDay === DAYS.length - 1}
              className="flex-shrink-0 p-2 rounded-lg border border-stone-700 bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              title="Jour suivant"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
          
          {/* Selected Day Content */}
          <div className="bg-stone-950 p-6 rounded-xl" style={{ backgroundColor: '#0a0a0a' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-bold text-white">{getDayLabel(selectedDay)}</h3>
              {currentDayTemplates.length > 0 && (
                <Button 
                  onClick={handleDeleteDayTemplates} 
                  variant="outline"
                  size="sm"
                  className="border-red-700 text-red-400 hover:bg-red-900/20"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Supprimer tous les créneaux du jour
                </Button>
              )}
            </div>
            
            {currentDayTemplates.length === 0 ? (
              <div className="text-center py-12 text-stone-500">
                <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Aucun créneau configuré pour {getDayLabel(selectedDay)}</p>
                <Button onClick={() => handleOpenModal()} className="mt-4 bg-white text-stone-900 hover:bg-stone-100">
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter le premier créneau
                </Button>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {currentDayTemplates.map((template) => {
                  const colors = getTypeColor(template.slot_type);
                  return (
                    <div
                      key={template.id}
                      style={{ backgroundColor: '#000000' }}
                      className={`rounded-lg ${colors.borderWidth} ${colors.border} p-4 transition-all hover:shadow-lg hover:shadow-stone-900/50`}
                    >
                      <div className="mb-2 flex items-center justify-between">
                        <div>
                          <span className={`text-sm font-semibold ${colors.text}`}>
                            {String(template.hour).padStart(2, '0')}:{String(template.minute).padStart(2, '0')}
                          </span>
                          <span className="ml-2 text-xs text-stone-500">
                            {template.duration_min} min
                          </span>
                        </div>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${colors.text}`}>
                          {getTypeLabel(template.slot_type)}
                        </span>
                      </div>
                      
                      <div className={`mb-2 text-xs font-medium ${colors.text}`}>
                        Capacité : {template.capacity} place{template.capacity > 1 ? 's' : ''}
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleOpenModal(template)}
                          className={`flex-1 text-xs ${colors.badge} border-0 text-white`}
                        >
                          <Pencil className="h-3 w-3 mr-1" />
                          Modifier
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(template.id)}
                          className="border-red-700 text-red-400 hover:bg-red-900/20 text-xs"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Ajouter/Modifier */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-black border-stone-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">{editingTemplate ? "Modifier le créneau" : "Ajouter un créneau"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-stone-300">Jour</Label>
              <Select value={String(formData.day)} onValueChange={(v) => setFormData({...formData, day: parseInt(v)})}>
                <SelectTrigger className="mt-1 bg-white text-stone-900 border-stone-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAYS.map(day => (
                    <SelectItem key={day.value} value={String(day.value)}>{day.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-stone-300">Heure</Label>
                <Input
                  type="number"
                  min="0"
                  max="23"
                  value={formData.hour}
                  onChange={(e) => setFormData({...formData, hour: parseInt(e.target.value)})}
                  className="mt-1 bg-white text-stone-900 border-stone-300"
                />
              </div>
              <div>
                <Label className="text-stone-300">Minutes</Label>
                <Input
                  type="number"
                  min="0"
                  max="59"
                  step="15"
                  value={formData.minute}
                  onChange={(e) => setFormData({...formData, minute: parseInt(e.target.value)})}
                  className="mt-1 bg-white text-stone-900 border-stone-300"
                />
              </div>
            </div>

            <div>
              <Label className="text-stone-300">Durée (minutes)</Label>
              <Input
                type="number"
                min="15"
                step="15"
                value={formData.duration_min}
                onChange={(e) => setFormData({...formData, duration_min: parseInt(e.target.value)})}
                className="mt-1 bg-white text-stone-900 border-stone-300"
              />
            </div>

            <div>
              <Label className="text-stone-300">Type</Label>
              <Select value={formData.slot_type} onValueChange={(v) => setFormData({...formData, slot_type: v})}>
                <SelectTrigger className="mt-1 bg-white text-stone-900 border-stone-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SLOT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-stone-300">Capacité (places)</Label>
              <Input
                type="number"
                min="1"
                value={formData.capacity}
                onChange={(e) => setFormData({...formData, capacity: parseInt(e.target.value)})}
                className="mt-1 bg-white text-stone-900 border-stone-300"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button onClick={handleSave} className="flex-1 bg-white text-stone-900 hover:bg-stone-100">
                {editingTemplate ? "Enregistrer" : "Ajouter"}
              </Button>
              <Button onClick={() => setModalOpen(false)} variant="outline" className="flex-1 border-stone-300 text-stone-300 hover:bg-stone-800">
                Annuler
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
